import * as vp from 'vopay';
import { FieldData } from '@/components/Form/types';
import Toggle from '@/components/UI/Toggle/Toggle';
import { ElinxSettingsInfo } from './ElinxSettingsForm';
import { useTranslation } from 'react-i18next';

const { Tooltip, TextField, Radio } = vp;

export function buildGeneralSettings(): FieldData<ElinxSettingsInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.SettingsDialogue.Form.GeneralSettings' });

  return [
    {
      label: () => t('TokenExpirationTime.label'),
      fieldName: 'TokenExpirationTime',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="number" {...fieldData} />;
      },
    },
    {
      label: () => t('IsDeclineEnabled.label'),
      fieldName: 'IsDeclineEnabled',
      getInputState: () => 'optional',
      showField: () => true,
      tooltip: () => <Tooltip information={t('IsDeclineEnabled.tooltip')} position="right" width="394px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return <Toggle name={fieldData.name} checked={fieldData.value} onChange={fieldData.onChange} />;
      },
    },
    {
      label: () => t('FlagDuplicateTransactions.label', { defaultValue: 'Flag Duplicate Transactions' }),
      fieldName: 'RejectDuplicateRequests',
      getInputState: () => 'optional',
      showField: () => true,
      tooltip: () => (
        <Tooltip
          information={t('FlagDuplicateTransactions.tooltip', {
            defaultValue:
              'When on, eLinx blocks new requests that match an active request with the same amount and contact.',
          })}
          position="right"
          width="394px"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return <Toggle name={fieldData.name} checked={fieldData.value} onChange={fieldData.onChange} />;
      },
    },
  ];
}

export function buildCardPayments(creditCardEnabled: boolean): FieldData<ElinxSettingsInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.SettingsDialogue.Form.CardPayments' });

  return [
    {
      label: () => t('Trigger3DS.label', { defaultValue: 'Require 3DS Authentication by default' }),
      fieldName: 'Trigger3DS',
      getInputState: () => 'optional',
      showField: () => creditCardEnabled,
      tooltip: () => (
        <Tooltip
          information={t('Trigger3DS.tooltip', {
            defaultValue:
              'Applies to credit card payments only. When on, new eLinx requests will ask the cardholder to verify the payment with their card issuer. You can turn it off for an individual eLinx.',
          })}
          position="right"
          width="295px"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return <Toggle name={fieldData.name} checked={fieldData.value} onChange={fieldData.onChange} />;
      },
    },
  ];
}

export function buildSenderNotifications(): FieldData<ElinxSettingsInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.SettingsDialogue.Form.SenderNotifications' });

  return [
    {
      label: () => t('SenderEmailAddress.label'),
      fieldName: 'SenderEmailAddress',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
    },
    {
      label: () => t('SenderNotifications.label'),
      fieldName: 'SenderNotifications',
      getInputState: () => 'optional',
      showField: () => true,
      tooltip: () => <Tooltip information={t('SenderNotifications.tooltip')} position="right" width="295px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return <Toggle name={fieldData.name} checked={fieldData.value} onChange={fieldData.onChange} />;
      },
    },
  ];
}

export function buildRecipientNotifications(): FieldData<ElinxSettingsInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.SettingsDialogue.Form.RecipientNotifications' });

  return [
    {
      label: () => t('ReceiverNotifications.label'),
      fieldName: 'ReceiverNotifications',
      getInputState: () => 'optional',
      showField: () => true,
      tooltip: () => <Tooltip information={t('ReceiverNotifications.tooltip')} position="right" width="295px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return <Toggle name={fieldData.name} checked={fieldData.value} onChange={fieldData.onChange} />;
      },
    },
    {
      label: () => t('ELinxReminderEnabled.label'),
      fieldName: 'ELinxReminderEnabled',
      getInputState: () => 'optional',
      showField: () => true,
      tooltip: () => <Tooltip information={t('ELinxReminderEnabled.tooltip')} position="right" width="327px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return <Toggle name={fieldData.name} checked={fieldData.value} onChange={fieldData.onChange} />;
      },
    },
    {
      label: () => t('ELinxReminderFrequency.label'),
      fieldName: 'ELinxReminderFrequency',
      getInputState: (ctx) => (ctx.values.ELinxReminderEnabled ? 'optional' : 'disabled'),
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="number" {...fieldData} />;
      },
    },
  ];
}

export function buildCommunications(
  defaultNotificationMethod: vp.ElinxNotificationMethod,
): FieldData<ElinxSettingsInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.SettingsDialogue.Form.Communications' });

  return [
    {
      label: () => t('DefaultNotificationMethod.label'),
      fieldName: 'DefaultNotificationMethod',
      getInputState: (ctx) => (ctx.values.ReceiverNotifications ? 'optional' : 'disabled'),
      showField: () => true,
      tooltip: () => <Tooltip information={t('DefaultNotificationMethod.tooltip')} position="right" width="272px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        const options = {
          email: t('DefaultNotificationMethod.Options.email'),
          sms: t('DefaultNotificationMethod.Options.sms'),
          ...(defaultNotificationMethod === 'whatsapp' && {
            whatsapp: t('DefaultNotificationMethod.Options.whatsapp'),
          }),
        };

        return <Radio variant="inline" options={options} {...fieldData} />;
      },
    },
  ];
}
